import { supabaseClient } from '../../../shared/utils/supabaseClient';

// Phase 3c session bridge — the single seam the member login screen calls for
// phone + PIN. POSTs { phone, pin } to the pin-login Edge Function, which resolves
// the member by phone, verifies the PIN, and mints a session; on success this turns
// the returned tokens into a real Supabase session via setSession(), so
// auth.getUser() and all auth.uid()-based RLS work identically to an email-OTP member.

const PIN_LOGIN_URL = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/pin-login`;
const ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

/**
 * @param {string} phone - raw phone (e.g. "2345678888" or "+12345678888"); normalized server-side
 * @param {string} pin   - 4-digit PIN
 * @returns {Promise<{success:true, member_id:number} | {success:false, reason:string, locked_until?:string}>}
 */
export const loginWithPin = async (phone, pin) => {
  let payload;
  try {
    const res = await fetch(PIN_LOGIN_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        // Supabase Functions gateway still expects the publishable/anon key to route.
        apikey: ANON_KEY,
        Authorization: `Bearer ${ANON_KEY}`,
      },
      body: JSON.stringify({ phone, pin }),
    });
    payload = await res.json().catch(() => null);

    // Any non-2xx or non-success response → surface the reason, no session.
    if (!res.ok || !payload || payload.success !== true) {
      return {
        success: false,
        reason: (payload && payload.reason) || 'error',
        ...(payload && payload.locked_until ? { locked_until: payload.locked_until } : {}),
      };
    }
  } catch {
    return { success: false, reason: 'network_error' };
  }

  // Establish the real Supabase session from the minted tokens.
  const { error } = await supabaseClient.auth.setSession({
    access_token: payload.access_token,
    refresh_token: payload.refresh_token,
  });
  if (error) {
    return { success: false, reason: 'session_error' };
  }

  return { success: true, member_id: payload.member_id };
};
